import React, { useEffect, useRef, useState } from 'react';
import { Steps, Card, Button, Flex, Result, Spin, Typography } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import IntlMessage from '../../components/IntlMessage/IntlMessage';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import { fetchJob } from '../../store/slices/lspivSlice';
import { clrs } from '../../constants/colors';
import classes from './lspiv.module.css';

import VideoStep from './steps/VideoStep';
import MarkupStep from './steps/MarkupStep';
import ResultStep from './steps/ResultStep';

const { Title, Text } = Typography;

const STEPS = [
    { key: 'video', titleKey: 'lspiv.steps.video', component: VideoStep },
    { key: 'markup', titleKey: 'lspiv.steps.markup', component: MarkupStep },
    { key: 'result', titleKey: 'lspiv.steps.result', component: ResultStep },
];

const POLL_MS = 3000;

// С какого шага открыть задание: нет кадров — с видео, есть расчёт — с результата.
const initialStep = (job) => {
    const step = job?.status?.step;
    if (!job?.frames_count || step === 'extract-frames') return 0;
    if (step === 'analysis' || step === 'done') return 2;
    return 1;
};

const LSPIVWizardPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { currentJob, isLoading } = useSelector((s) => s.lspiv);
    const [stepIndex, setStepIndex] = useState(null);
    const initializedFor = useRef(null);

    const job = currentJob?.job_id === jobId ? currentJob : null;
    const running = job?.status?.status === 'running';

    useEffect(() => {
        if (jobId) dispatch(fetchJob(jobId));
    }, [dispatch, jobId]);

    useEffect(() => {
        if (job && initializedFor.current !== jobId) {
            initializedFor.current = jobId;
            setStepIndex(initialStep(job));
        }
    }, [job, jobId]);

    // Пока сервер извлекает кадры или считает — опрашиваем статус.
    useEffect(() => {
        if (!running) return undefined;
        const timer = setInterval(() => dispatch(fetchJob(jobId)), POLL_MS);
        return () => clearInterval(timer);
    }, [running, dispatch, jobId]);

    const refresh = () => dispatch(fetchJob(jobId));

    if (!job && isLoading) {
        return (
            <div>
                <Header />
                <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
                    <Spin size="large" />
                </Flex>
                <Footer />
            </div>
        );
    }

    if (!job) {
        return (
            <div>
                <Header />
                <div className={classes.page}>
                    <Result
                        status="404"
                        title={t('lspiv.wizard.notFoundTitle')}
                        subTitle={t('lspiv.wizard.notFoundSubtitle', { jobId })}
                        extra={<Button type="primary" onClick={() => navigate('/lspiv')}>{t('lspiv.wizard.toList')}</Button>}
                    />
                </div>
                <Footer />
            </div>
        );
    }

    const current = stepIndex ?? initialStep(job);
    const StepComponent = STEPS[current].component;
    const hasFrames = job.frames_count > 0 && job.status?.step !== 'extract-frames';
    const hasRun = ['analysis', 'done'].includes(job.status?.step);

    return (
        <div>
            <Header />
            <div className={classes.page}>
                <div className={classes.wrapper}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
                        <div>
                            <Title level={3} style={{ margin: 0, color: clrs.textColor }}>
                                <IntlMessage id='lspiv.wizard.jobTitle' />{jobId}
                            </Title>
                            <Text type="secondary">{t(`lspiv.stepName.${job.status?.step || 'created'}`, job.status?.step || '')}</Text>
                        </div>
                        <Button onClick={() => navigate('/lspiv')}>{t('lspiv.wizard.toList')}</Button>
                    </Flex>

                    <Card className={classes.card} styles={{ body: { padding: '24px' } }}>
                        <Steps
                            current={current}
                            onChange={setStepIndex}
                            items={STEPS.map((s, i) => ({
                                title: t(s.titleKey),
                                disabled: (i === 1 && !hasFrames) || (i === 2 && !hasRun),
                            }))}
                            className={classes.stepsContainer}
                        />

                        <StepComponent
                            jobId={jobId}
                            job={job}
                            onRefresh={refresh}
                            onNext={() => setStepIndex(Math.min(current + 1, STEPS.length - 1))}
                            onBack={() => setStepIndex(Math.max(current - 1, 0))}
                        />
                    </Card>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default LSPIVWizardPage;
